import { Injectable, inject } from '@angular/core';
import { BaseApiService, PaginatedResults } from '@bo/ng-api';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  Employee,
  EmployeeIdentityDocument,
  EmployeeIdentityDocumentRequest,
  EmployeeInviteResponse,
  EmployeeRequest,
  FileUploadData,
  FileUploadErrors,
  FileUploadResponse,
  ReplaceApproverRequest,
} from '../../../_interfaces/employee.interface';
import { map, Observable, tap } from 'rxjs';
import { FileDownloaderService } from '../../file-downloader.service';
import {
  ValidTokenData,
  ValidTokenResponse,
  VerifyAccountData,
  VerifyAccountResponse,
} from '../../../_interfaces/verify-account';

@Injectable({
  providedIn: 'root',
})
export class EmployeeApiService extends BaseApiService {
  protected override http: HttpClient;
  readonly fileDownloader = inject(FileDownloaderService);

  override baseUrl: string = environment.apiRoot;
  URL_EMPLOYEE_SIGNUP: string = 'employee-signup';

  // relative to URL_EMPLOYEE_SIGNUP/PK
  URL_VERIFY: string = 'verify';
  URL_VALID: string = 'valid';
  URL_RESEND: string = 'resend';

  URL_EMPLOYEE: string = 'employee';

  URL_REPLACE_APPROVER: string = 'replace-approver'; // relative to URL_EMPLOYEE/PK

  URL_EMPLOYEE_FILE: string = 'employee-file';
  // relative to URL_EMPLOYEE_FILE/PK
  URL_TEMPLATE: string = 'template';
  URL_ERRORS: string = 'errors';
  URL_ERRORS_FILE: string = 'errors-file';

  URL_EMPLOYEE_IDENTITY_DOCUMENT: string = 'employee-identity-document';

  constructor() {
    const http = inject(HttpClient);

    super(http);

    this.http = http;
  }

  /**
   * Invite/create an employee
   *
   * @param data
   * @return Observable<Employee>
   */
  inviteEmployee(data: EmployeeRequest): Observable<EmployeeRequest> {
    const url = this.getUrl(this.URL_EMPLOYEE_SIGNUP, true);
    return this.http.post<EmployeeRequest>(url, data);
  }

  /**
   * Resend an employee invitation
   *
   * @param employeeId
   * @return Observable<EmployeeInviteResponse>
   */
  resendEmployeeInvite(employeeId: string) {
    const url = this.getUrl([this.URL_EMPLOYEE_SIGNUP, employeeId, this.URL_RESEND], true);
    return this.http.post<EmployeeInviteResponse>(url, {});
  }

  /**
   * Remove an employee invitation
   *
   * @return Observable<Record<PropertyKey, never>>
   * @param empId
   */
  removeInvitedEmployee(empId: string): Observable<Record<PropertyKey, never>> {
    const url = this.getUrl([this.URL_EMPLOYEE_SIGNUP, empId], true);
    return this.http.delete<Record<PropertyKey, never>>(url);
  }

  /**
   * Request invited employee details by ID
   *
   * @param empId
   * @return Observable<Employee>
   */
  getInvitedEmployeeById(empId: string): Observable<EmployeeRequest> {
    const url = this.getUrl([this.URL_EMPLOYEE_SIGNUP, empId], true);
    return this.http.get<EmployeeRequest>(url);
  }

  /**
   * Update ivited employee details
   *
   * @param empId
   * @param data
   * @return Observable<EmployeeRequest>
   */
  updateInvitedEmployee(empId: string, data: EmployeeRequest): Observable<EmployeeRequest> {
    const url = this.getUrl([this.URL_EMPLOYEE_SIGNUP, empId], true);
    return this.http.put<EmployeeRequest>(url, data);
  }

  /**
   * Get all employee invitations - Paginated
   *
   * @param queryParams
   * @return Observable<Employee[]>
   */
  getInvitedEmployees(queryParams: HttpParams): Observable<PaginatedResults<Employee[]>> {
    const url = this.getUrl(this.URL_EMPLOYEE_SIGNUP, true);
    return this.http.get<Employee[]>(url, { observe: 'response', params: queryParams }).pipe(
      map((res) => {
        return new PaginatedResults<Employee[]>(res);
      }),
    );
  }

  /**
   * Verify Employee email and create password
   *
   * @param signUpId
   * @param data
   * @return Observable<VerifyAccountResponse>
   */
  verifyEmployeeAccount(signUpId: string, data: VerifyAccountData): Observable<VerifyAccountResponse> {
    const url = this.getUrl([this.URL_EMPLOYEE_SIGNUP, signUpId, this.URL_VERIFY], true);
    return this.http.post<VerifyAccountResponse>(url, data);
  }

  verifyEmployeeAccountTokenValid(signupID: string, data: ValidTokenData) {
    const url = this.getUrl([this.URL_EMPLOYEE_SIGNUP, signupID, this.URL_VALID], true);
    return this.http.post<ValidTokenResponse>(url, data);
  }

  /**
   * Update employee details
   *
   * @param empId
   * @param data
   * @return Observable<EmployeeRequest>
   */
  updateEmployee(empId: string, data: EmployeeRequest): Observable<EmployeeRequest> {
    const url = this.getUrl([this.URL_EMPLOYEE, empId], true);
    return this.http.patch<EmployeeRequest>(url, data);
  }

  /**
   * Request employee details by ID
   *
   * @param empId
   * @return Observable<EmployeeRequest>
   */
  getEmployeeById(empId: string): Observable<Employee> {
    const url = this.getUrl([this.URL_EMPLOYEE, empId], true);
    return this.http.get<Employee>(url);
  }

  /**
   * Get all onboarded employees
   *
   * @return Observable<Employee[]>
   */
  getAllEmployees(): Observable<Employee[]> {
    const url = this.getUrl(this.URL_EMPLOYEE, true);
    return this.http.get<Employee[]>(url);
  }

  /**
   * Get all onboarded employees - Paginated
   *
   * @return Observable<PaginatedResults<Employee[]>>
   * @param queryParams
   */
  getEmployees(queryParams: HttpParams): Observable<PaginatedResults<Employee[]>> {
    const url = this.getUrl(this.URL_EMPLOYEE, true);
    return this.http.get<Employee[]>(url, { observe: 'response', params: queryParams }).pipe(
      map((res) => {
        return new PaginatedResults<Employee[]>(res);
      }),
    );
  }

  /**
   * Replace an employee approver
   *
   * @return Observable<Record<PropertyKey, never>>
   * @param empId
   * @param empReplaceId
   */
  replaceEmployeeApprover(empId: string, empReplaceId: string): Observable<ReplaceApproverRequest> {
    const url = this.getUrl([this.URL_EMPLOYEE, empId, this.URL_REPLACE_APPROVER], true);
    return this.http.post<ReplaceApproverRequest>(url, {
      replacement: empReplaceId,
    });
  }

  /**
   * Remove an employee
   *
   * @return Observable<Record<PropertyKey, never>>
   * @param empId
   */
  removeEmployee(empId: string): Observable<Record<PropertyKey, never>> {
    const url = this.getUrl([this.URL_EMPLOYEE, empId], true);
    return this.http.delete<Record<PropertyKey, never>>(url);
  }

  /**
   * Download example csv template for bulk upload
   *
   * @return Observable<Blob>
   */
  downloadBulkEmployeeFileTemplate(): Observable<Blob> {
    const url = this.getUrl([this.URL_EMPLOYEE_FILE, this.URL_TEMPLATE], true);
    return this.http.get(url, { responseType: 'blob' }).pipe(
      tap((blob: Blob) => {
        this.fileDownloader.download(blob, 'Virtual_card_holder_template');
      }),
    );
  }

  /**
   * Upload csv file with employee data (bulk upload)
   *
   * @param file
   * @return Observable<FileUploadData>
   */
  uploadBulkEmployeeFile(file: FileUploadData): Observable<FileUploadData> {
    const url = this.getUrl(this.URL_EMPLOYEE_FILE, true);
    return this.http.post<FileUploadData>(url, file);
  }

  /**
   * Get information of the uploaded csv bulk employee file (while, and after it's been processed)
   *
   * @param id
   * @return Observable<FileUploadResponse>
   */
  getBulkEmployeeFile(id: number): Observable<FileUploadResponse> {
    const url = this.getUrl([this.URL_EMPLOYEE_FILE, id], true);
    return this.http.get<FileUploadResponse>(url);
  }

  /**
   * Download csv file containing file errors
   *
   * @param fileId
   * @return Observable<Blob>
   */
  downloadBulkEmployeeFileErrors(fileId: number): Observable<Blob> {
    const url = this.getUrl([this.URL_EMPLOYEE_FILE, fileId, this.URL_ERRORS_FILE], true);
    return this.http.get(url, { responseType: 'blob' }).pipe(
      tap((blob: Blob) => {
        this.fileDownloader.download(blob, 'Employee_data_errors');
      }),
    );
  }

  /**
   * Get detailed file error data if the file return errors
   *
   * @param fileId
   * @return Observable<FileUploadErrors>
   */
  getBulkEmployeeFileErrors(fileId: number): Observable<FileUploadErrors[]> {
    const url = this.getUrl([this.URL_EMPLOYEE_FILE, fileId, this.URL_ERRORS], true);
    return this.http.get<FileUploadErrors[]>(url);
  }

  /**
   * Get employee identity Document data
   *
   * @return Observable<FileUploadErrors>
   */
  getEmployeeIdentityDocument(): Observable<EmployeeIdentityDocument> {
    const url = this.getUrl(this.URL_EMPLOYEE_IDENTITY_DOCUMENT, true);
    return this.http.get<EmployeeIdentityDocument>(url);
  }

  /**
   * Update employee identity Document data
   * @param data
   */
  postEmployeeIdentityDocument(data: EmployeeIdentityDocumentRequest): Observable<EmployeeIdentityDocument> {
    const url = this.getUrl(this.URL_EMPLOYEE_IDENTITY_DOCUMENT, true);
    return this.http.post<EmployeeIdentityDocument>(url, data);
  }
}
